import {
  type ChangeEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import type { TickerSuggestion } from "@/shared/lib/types";
import styles from "./TickerLookupField.module.css";

export interface TickerLookupFieldProps {
  value: string;
  /** Parent-owned value confirmed by selecting a current lookup result. */
  confirmedValue?: string;
  onValueChange: (value: string) => void;
  onSelect: (suggestion: TickerSuggestion) => void;
  searchTickers?: (query: string) => Promise<TickerSuggestion[]>;
  label: string;
  ariaLabel?: string;
  /** Marks the input invalid after an attempted form transition. */
  invalid?: boolean;
  /** ID of the visible validation message associated with the input. */
  errorMessageId?: string;
  /** Focuses the input on mount. Reserved for the single ticker-first entry point. */
  autoFocus?: boolean;
}

type LookupState = "idle" | "loading" | "success" | "empty" | "error";

const normalizeTicker = (value: string) => value.toUpperCase();

export default function TickerLookupField({
  value,
  confirmedValue,
  onValueChange,
  onSelect,
  searchTickers,
  label,
  ariaLabel,
  invalid = false,
  errorMessageId,
  autoFocus = false,
}: TickerLookupFieldProps) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const statusId = useId();
  const requestIdRef = useRef(0);
  const [suggestions, setSuggestions] = useState<TickerSuggestion[]>([]);
  const [lookupState, setLookupState] = useState<LookupState>("idle");
  const [resultQuery, setResultQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedSuggestion, setSelectedSuggestion] = useState<TickerSuggestion | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const query = value.trim();
  const hasLookup = Boolean(searchTickers);
  const visibleSuggestions = resultQuery === query ? suggestions : [];
  const visibleLookupState =
    resultQuery === query ? lookupState : ("idle" as const);
  const isExpanded =
    hasLookup && isFocused && visibleSuggestions.length > 0;

  useEffect(() => {
    if (!searchTickers || !query) {
      requestIdRef.current += 1;
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;

    const timer = window.setTimeout(async () => {
      setResultQuery(query);
      setSuggestions([]);
      setLookupState("loading");

      try {
        const results = await searchTickers(query);
        if (cancelled || requestId !== requestIdRef.current) return;

        const validSuggestions = results.filter(
          (suggestion) =>
            typeof suggestion.ticker === "string" &&
            suggestion.ticker.trim().length > 0,
        );
        setSuggestions(validSuggestions);
        setLookupState(validSuggestions.length > 0 ? "success" : "empty");
      } catch {
        if (cancelled || requestId !== requestIdRef.current) return;

        setSuggestions([]);
        setLookupState("error");
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, retryNonce, searchTickers]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
    // Intentionally runs once on mount: this field is ticker-first on its
    // screen, so it should claim focus only when it first appears, never on
    // every value/prop change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestCurrentQuery = () => {
    if (!hasLookup || !query || visibleSuggestions.length > 0) return;
    setRetryNonce((value) => value + 1);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = normalizeTicker(event.target.value);
    setActiveIndex(-1);
    if (nextValue.trim() !== selectedSuggestion?.ticker.trim().toUpperCase()) {
      setSelectedSuggestion(null);
    }
    onValueChange(nextValue);
  };

  const selectSuggestion = (suggestion: TickerSuggestion) => {
    const ticker =
      typeof suggestion.ticker === "string"
        ? normalizeTicker(suggestion.ticker.trim())
        : "";
    if (!ticker) return;

    requestIdRef.current += 1;
    setSelectedSuggestion({ ...suggestion, ticker });
    onValueChange(ticker);
    onSelect(suggestion);
    setSuggestions([]);
    setLookupState("idle");
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!hasLookup) return;

    if (event.key === "Escape") {
      if (isExpanded) event.preventDefault();
      requestIdRef.current += 1;
      setSuggestions([]);
      setResultQuery("");
      setLookupState("idle");
      setActiveIndex(-1);
      return;
    }

    if (!isExpanded) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        requestCurrentQuery();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex(
        (current) => (current + 1) % visibleSuggestions.length,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? visibleSuggestions.length - 1 : current - 1,
      );
      return;
    }

    if (event.key === "Enter" && activeIndex >= 0) {
      event.preventDefault();
      selectSuggestion(visibleSuggestions[activeIndex]);
    }
  };

  const statusText = (() => {
    if (!hasLookup) return "";
    if (
      selectedSuggestion
      && selectedSuggestion.ticker.trim().toUpperCase() === query.toUpperCase()
      && (confirmedValue === undefined
        || confirmedValue.trim().toUpperCase() === query.toUpperCase())
      && visibleLookupState === "idle"
    ) {
      return selectedSuggestion.name
        ? `Selected: ${selectedSuggestion.ticker} — ${selectedSuggestion.name}`
        : `Selected: ${selectedSuggestion.ticker}`;
    }
    if (visibleLookupState === "loading") return "Searching for tickers…";
    if (visibleLookupState === "empty") {
      return `No tickers found for ${query}.`;
    }
    if (visibleLookupState === "error") {
      return "Ticker lookup is unavailable.";
    }
    if (visibleLookupState === "success") {
      return `${visibleSuggestions.length} ticker${visibleSuggestions.length === 1 ? "" : "s"} found.`;
    }
    return "";
  })();

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <div className={styles.control}>
        <input
          ref={inputRef}
          id={inputId}
          className={styles.input}
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => {
            setIsFocused(true);
            requestCurrentQuery();
          }}
          onBlur={() => {
            setIsFocused(false);
            setActiveIndex(-1);
          }}
          onKeyDown={handleKeyDown}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          aria-describedby={[
            hasLookup ? statusId : null,
            invalid ? errorMessageId : null,
          ].filter(Boolean).join(" ") || undefined}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          {...(hasLookup
            ? {
                role: "combobox",
                "aria-autocomplete": "list" as const,
                "aria-expanded": isExpanded,
                "aria-controls": listboxId,
                "aria-activedescendant":
                  isExpanded && activeIndex >= 0
                    ? `${listboxId}-option-${activeIndex}`
                    : undefined,
              }
            : {})}
        />

        {visibleLookupState === "loading" ? (
          <span className={styles.spinner} aria-hidden="true" />
        ) : null}

        {isExpanded ? (
          <ul className={styles.listbox} id={listboxId} role="listbox">
            {visibleSuggestions.map((suggestion, index) => {
              const ticker = String(suggestion.ticker);
              const name =
                typeof suggestion.name === "string" ? suggestion.name : "";
              const optionId = `${listboxId}-option-${index}`;
              return (
                <li
                  className={styles.option}
                  data-active={activeIndex === index || undefined}
                  id={optionId}
                  key={`${ticker}-${name}-${index}`}
                  role="option"
                  aria-selected={activeIndex === index}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <strong>{ticker}</strong>
                  {name ? <span>{name}</span> : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {hasLookup ? (
        <div
          className={styles.status}
          id={statusId}
          role="status"
          aria-live="polite"
        >
          <span>{statusText}</span>
          {visibleLookupState === "error" ? (
            <button type="button" onClick={() => setRetryNonce((value) => value + 1)}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
